import api from '../utils/api';
import authService from './authService';

const tableService = {
  getAllTables: async (filters = {}) => {
    try {
      const currentUser = authService.getUserFromStorage();
      
      let url = '/tables';
      const queryParams = new URLSearchParams();
      
      if (filters.branchId) {
        queryParams.append('branchId', filters.branchId);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.section) {
        queryParams.append('section', filters.section);
      }
      
      if (currentUser && (currentUser.role === 'manager' || currentUser.role === 'waiter')) {
        if (currentUser.branchId) {
          queryParams.set('branchId', currentUser.branchId);
        }
      }
      
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
      
      const response = await api.get(url);
      
      if (Array.isArray(response.data)) {
        response.data.forEach(table => {
          if (table._id && !table.id) {
            table.id = table._id;
          }
        });
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getTablesByBranch: async (branchId) => {
    try {
      if (!branchId) {
        throw new Error('Branch ID is required');
      }
      
      const currentUser = authService.getUserFromStorage();

      const normalizedBranchId = String(branchId);

      // Users assigned via assigned_branch_ids (branch_admin pattern) must
      // pass this gate too — Istanbul 2026-07-31: a branch_admin got an
      // EMPTY table list on the web POS because this check only knew about
      // branchId/branchPermissions. Mirrors menuService's derivation; the
      // backend enforces real authorization regardless.
      const assignedBranchIds = (
        currentUser?.assigned_branch_ids ||
        currentUser?.assignedBranchIds ||
        currentUser?.branch_ids || []
      ).map(String);
      const isAssignedViaList = assignedBranchIds.includes(normalizedBranchId);

      if (currentUser && currentUser.role !== 'owner' && !currentUser.permissions?.manageRestaurants && !isAssignedViaList) {
        if (String(currentUser.branchId) !== normalizedBranchId) {
          const hasBranchAccess = currentUser.branchPermissions?.tables?.some(id =>
            String(id) === normalizedBranchId
          );

          if (!hasBranchAccess) {
            console.warn("User doesn't have permission to access this branch's tables");
            return [];
          }
        }
      }
      
      const response = await api.get(`/tables?branchId=${normalizedBranchId}`);
      
      if (Array.isArray(response.data)) {
        response.data.forEach(table => {
          if (table._id && !table.id) {
            table.id = table._id;
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching tables for branch ${branchId}:`, error);
      return [];
    }
  },
  
  getTableById: async (id) => {
    try {
      const currentUser = authService.getUserFromStorage();
      
      const response = await api.get(`/tables/${id}`);
      const table = response.data;
      
      if (table && table._id && !table.id) {
        table.id = table._id;
      }
      
      if (table && currentUser && currentUser.role !== 'owner' && !currentUser.permissions?.manageRestaurants) {
        if (currentUser.branchId !== table.branchId) {
          if (currentUser.branchPermissions?.tables && 
              currentUser.branchPermissions.tables.length > 0 &&
              !currentUser.branchPermissions.tables.includes(table.branchId)) {
            throw new Error("Access denied: You don't have permission to access this table");
          }
        }
      }
      
      return table;
    } catch (error) {
      throw error;
    }
  },
  
  createTable: async (tableData) => {
    try {
      const currentUser = authService.getUserFromStorage();
      
      if (tableData.branchId && currentUser) {
        const branchId = tableData.branchId;
        
        if (currentUser.role !== 'owner' && !currentUser.permissions?.manageRestaurants) {
          if (currentUser.role === 'manager') {
            if (!currentUser.permissions?.manageTables || currentUser.branchId !== branchId) {
              throw new Error("Access denied: You don't have permission to create tables for this branch");
            }
          } else if (currentUser.branchPermissions?.tables) {
            if (!currentUser.branchPermissions.tables.includes(branchId)) {
              throw new Error("Access denied: You don't have permission to create tables for this branch");
            }
          } else {
            throw new Error("Access denied: You don't have permission to create tables");
          }
        }
      }
      
      const response = await api.post('/tables', tableData);
     
      if (response.data && response.data._id && !response.data.id) {
        response.data.id = response.data._id;
      }
     
      return response.data;
   } catch (error) {
     throw error;
   }
 },
 
 updateTable: async (id, tableData) => {
   try {
     const currentUser = authService.getUserFromStorage();
     
     const table = await tableService.getTableById(id);
     
     if (currentUser && currentUser.role !== 'owner') {
       if (currentUser.role === 'manager') {
         if (!currentUser.permissions?.manageTables || currentUser.branchId !== table.branchId) {
           throw new Error("Access denied: You don't have permission to update this table");
         }
       } else if (currentUser.branchPermissions?.tables) {
         if (!currentUser.branchPermissions.tables.includes(table.branchId)) {
           throw new Error("Access denied: You don't have permission to update this table");
         }
       } else {
         throw new Error("Access denied: You don't have permission to update tables");
       }
     }
     
     const response = await api.put(`/tables/${id}`, tableData);
     
     if (response.data && response.data._id && !response.data.id) {
       response.data.id = response.data._id;
     }
     
     return response.data;
   } catch (error) {
     throw error;
   }
 },
 
 deleteTable: async (id) => {
   try {
     const currentUser = authService.getUserFromStorage();
     
     const table = await tableService.getTableById(id);
     
     if (currentUser && currentUser.role !== 'owner') {
       if (currentUser.role === 'manager') {
         if (!currentUser.permissions?.manageTables || currentUser.branchId !== table.branchId) {
           throw new Error("Access denied: You don't have permission to delete this table");
         }
       } else {
         throw new Error("Access denied: You don't have permission to delete tables");
       }
     }
     
     const response = await api.delete(`/tables/${id}`);
     return response.data;
   } catch (error) {
     throw error;
   }
 },
 
   updateTableStatus: async (id, status) => {
    try {
      return await tableService.updateTable(id, { status });
    } catch (error) {
      throw error;
    }
  },

  // Get tables with active order counts (for POS)
  getTablesWithOrderCounts: async (branchId) => {
    try {
      if (!branchId) {
        throw new Error('Branch ID is required');
      }
      
      const response = await api.get(`/tables/with-counts?branchId=${branchId}`);
      
      if (Array.isArray(response.data)) {
        response.data.forEach(table => {
          if (table._id && !table.id) {
            table.id = table._id;
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching tables with order counts for branch ${branchId}:`, error);
      return [];
    }
  },

  // Get tables grouped by section for formation management
  getTablesBySection: async (branchId) => {
    try {
      if (!branchId) {
        throw new Error('Branch ID is required');
      }
      
      const response = await api.get(`/tables/formation/${branchId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tables by section for branch ${branchId}:`, error);
      throw error;
    }
  },

  // Create multiple tables for a section
  createTablesForSection: async (data) => {
    try {
      console.log('Frontend: Creating tables for section:', data);
      const response = await api.post('/tables/formation', data);
      console.log('Frontend: Response received:', response.data);
      return response.data;
    } catch (error) {
      console.error('Frontend: Error creating tables for section:', error);
      console.error('Frontend: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      throw error;
    }
  },

  // Update table section and number
  updateTableSection: async (tableId, data) => {
    try {
      const response = await api.put(`/tables/${tableId}/section`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating table section:', error);
      throw error;
    }
  },

  // Get next available table number
  getNextTableNumber: async (branchId) => {
    try {
      const response = await api.get(`/tables/next-number?branchId=${branchId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting next table number:', error);
      throw error;
    }
  },

  // Get table formation mode for a branch
  getTableFormationMode: async (branchId) => {
    try {
      const response = await api.get(`/tables/formation-mode/${branchId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting table formation mode:', error);
      throw error;
    }
  },

  // Convert table formation mode for a branch
  convertTableFormationMode: async (branchId, mode) => {
    try {
      console.log('Frontend: Converting table formation mode:', { branchId, mode });
      
      const response = await api.post(`/tables/formation-mode/${branchId}/convert`, { mode });
      
      console.log('Frontend: Conversion successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('Frontend: Error converting table formation mode:', error);
      console.error('Frontend: Error details:', error.response?.data || error.message);
      throw error;
    }
  },

  // Custom Section Management
  getBranchSections: async (branchId) => {
    try {
      const response = await api.get(`/tables/sections/${branchId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching branch sections:', error);
      throw error;
    }
  },

  createCustomSection: async (branchId, sectionName) => {
    try {
      const response = await api.post(`/tables/sections/${branchId}`, { sectionName });
      return response.data;
    } catch (error) {
      console.error('Error creating custom section:', error);
      throw error;
    }
  },

  deleteCustomSection: async (branchId, sectionName) => {
    try {
      const response = await api.delete(`/tables/sections/${branchId}/${encodeURIComponent(sectionName)}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting custom section:', error);
      throw error;
    }
  },

  assignTablesToSection: async (branchId, tableIds, sectionName) => {
    try {
      const response = await api.post(`/tables/assign-section/${branchId}`, { 
        tableIds, 
        sectionName 
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning tables to section:', error);
      throw error;
    }
  }
};

export default tableService;